import { Field } from "@/components/molecules/field";
import { IntegrationLogo } from "@/components/molecules/integration-logo";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { getIntegration } from "@/features/integrations/catalog";
import { useSaveBookingAccount } from "@/features/integrations/hooks/use-booking-accounts";
import type {
  CheckedIntegration,
  IntegrationCheck,
} from "@/features/integrations/types";
import { useTranslation } from "@/features/utils/i18n";
import {
  CheckCircleIcon,
  EyeIcon,
  EyeSlashIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

type FieldSpec = {
  name: string;
  kind: "url" | "text" | "password";
  required?: boolean;
  /**
   * Deduced by the server when it recognises the rest: folded away until the
   * check says it could not, or the user asks to type it.
   */
  deduced?: boolean;
};

/**
 * The fields each integration's account is made of — and nothing else: the
 * integration's own settings (how many days of agenda to copy) stay on the
 * tool's page.
 */
const CONFIGURATORS: Record<CheckedIntegration, FieldSpec[]> = {
  provalliance_backoffice: [
    { name: "provalliance_public_url", kind: "url", required: true },
    { name: "provalliance_login", kind: "text", required: true },
    { name: "provalliance_password", kind: "password", required: true },
    { name: "provalliance_backoffice_url", kind: "url", deduced: true },
    { name: "provalliance_brand", kind: "text", deduced: true },
    { name: "provalliance_salon_id", kind: "text", deduced: true },
  ],
  calendly: [{ name: "calendly_token", kind: "password", required: true }],
  virtual_browser: [
    { name: "virtual_browser_url", kind: "url", required: true },
    { name: "virtual_browser_login", kind: "text" },
    { name: "virtual_browser_password", kind: "password" },
  ],
};

/**
 * The field whose change makes the deduced ones stale: a new public URL is a new
 * salon, and the old back-office and salon id must not be sent along with it.
 */
const DEDUCED_FROM: Partial<Record<CheckedIntegration, string>> = {
  provalliance_backoffice: "provalliance_public_url",
};

export const isConfigurable = (
  integration: string | undefined,
): integration is CheckedIntegration =>
  !!integration && integration in CONFIGURATORS;

/** Every field an integration's account is made of. */
export const configuratorFields = (integration: CheckedIntegration) =>
  CONFIGURATORS[integration].map((field) => field.name);

/** Is the account filled in enough to run? (Not whether it works: that is the check.) */
export const isConfigured = (
  integration: CheckedIntegration,
  data: Record<string, unknown>,
) =>
  CONFIGURATORS[integration]
    .filter((field) => field.required)
    .every((field) => !!String(data[field.name] ?? "").trim());

const pick = (integration: CheckedIntegration, data: Record<string, unknown>) =>
  Object.fromEntries(
    CONFIGURATORS[integration].map((field) => [
      field.name,
      String(data[field.name] ?? ""),
    ]),
  );

/**
 * One booking software's account, in a modal of its own: its logo, its fields
 * and no other, checked with the provider before it is kept.
 *
 * The button sends what was typed to the server, which logs in for real
 * (`integration-check.ts`) and stores the external account only when the
 * provider said yes — resolved as the server checked it (the Provalliance
 * back-office deduced from the public URL, a URL given its scheme). The proof
 * (the salon's hairdressers, the Calendly owner) stays on screen until "Terminé".
 *
 * Editing an account (`accountId`) never shows its secret: the page is never sent
 * it, and an empty one keeps the stored one. A provider that does not answer is
 * not the account's fault: that one refusal can be overridden.
 */
export function IntegrationConfigurator({
  integration,
  open,
  onOpenChange,
  accountId,
  initialValues,
  onSaved,
}: {
  integration: CheckedIntegration;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The account being edited; absent to connect a new one. */
  accountId?: string;
  /** The draft's starting point: the account's fields, or a configuration to convert. */
  initialValues: Record<string, unknown>;
  onSaved?: (accountId: string) => void;
}) {
  const { t } = useTranslation("tools");
  const save = useSaveBookingAccount();
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    pick(integration, initialValues),
  );
  const [result, setResult] = useState<IntegrationCheck | null>(null);
  const [savedId, setSavedId] = useState<string | null>(null);
  const [manual, setManual] = useState(false);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});

  // A fresh draft every time the modal opens: cancelling leaves nothing behind.
  useEffect(() => {
    if (!open) return;
    const values = pick(integration, initialValues);
    setDraft(values);
    setResult(null);
    setSavedId(null);
    setRevealed({});
    save.reset();
    // A configuration typed by hand before the deduction existed stays visible.
    const source = DEDUCED_FROM[integration];
    setManual(
      CONFIGURATORS[integration].some(
        (field) => field.deduced && values[field.name],
      ) &&
        !!source &&
        !values[source],
    );
    // Only on opening: the values are read once, as the draft's starting point.
  }, [open, integration]);

  const fields = CONFIGURATORS[integration];
  const hasDeduced = fields.some((field) => field.deduced);
  const showDeduced =
    manual ||
    result?.recognized === false ||
    fields.some(
      (field) => field.deduced && field.name === result?.error?.field,
    );
  const brand = getIntegration(integration);
  const name = t(`tools.integrations.${integration}.name`, brand?.name ?? "");

  const update = (field: string, value: string) => {
    setResult(null);
    setSavedId(null);
    setDraft((current) => {
      const next = { ...current, [field]: value };
      // A new public URL is a new salon: unless the user is typing everything,
      // what was deduced from the old one goes.
      if (field === DEDUCED_FROM[integration] && !manual) {
        for (const spec of fields) if (spec.deduced) next[spec.name] = "";
      }
      return next;
    });
  };

  const runSave = async (unchecked = false) => {
    const answer = await save.mutateAsync({
      integration,
      configuration: draft,
      unchecked,
      accountId,
    });
    setResult(answer.check);
    if (answer.account_id) {
      setSavedId(answer.account_id);
      // Kept without the provider's word: nothing to show, done.
      if (!answer.check.ok) finish(answer.account_id);
      return;
    }
    // What the server could deduce shows up in the fields it reveals, so an
    // unknown brand's user only types what is really missing.
    const check = answer.check;
    if (!check.ok && check.recognized === false) {
      setDraft((current) => {
        const next = { ...current };
        for (const spec of fields) {
          const resolved = check.configuration[spec.name];
          if (spec.deduced && !next[spec.name] && resolved) {
            next[spec.name] = String(resolved);
          }
        }
        return next;
      });
    }
  };

  const finish = (id: string) => {
    onSaved?.(id);
    onOpenChange(false);
  };

  // Editing never holds the stored secret: an empty one keeps it.
  const isRequired = (spec: FieldSpec) =>
    !!spec.required && !(accountId && spec.kind === "password");
  const missingRequired = fields.some(
    (spec) => isRequired(spec) && !draft[spec.name]?.trim(),
  );
  const errorFields = new Set([
    ...(result?.error?.field ? [result.error.field] : []),
    ...(result?.error?.missing ?? []),
  ]);

  const renderField = (spec: FieldSpec) => {
    const id = `integration-${spec.name}`;
    const secret = spec.kind === "password";
    const visible = revealed[spec.name];
    return (
      <Field
        key={spec.name}
        label={t(`tools.integrations.fields.${spec.name}.label`)}
        hint={t(`tools.integrations.fields.${spec.name}.hint`, "") || undefined}
        htmlFor={id}
        required={isRequired(spec)}
        error={
          errorFields.has(spec.name) && result?.error
            ? t(`tools.integrations.check.errors.${result.error.code}`, {
                name,
              })
            : undefined
        }
      >
        <div className="relative">
          <Input
            id={id}
            type={secret && !visible ? "password" : "text"}
            inputMode={spec.kind === "url" ? "url" : undefined}
            autoComplete={secret ? "new-password" : "off"}
            spellCheck={false}
            placeholder={
              (secret && accountId
                ? t("tools.integrations.configurator.secretUnchanged")
                : t(
                    `tools.integrations.fields.${spec.name}.placeholder`,
                    "",
                  )) || undefined
            }
            value={draft[spec.name] ?? ""}
            onChange={(e) => update(spec.name, e.target.value)}
            aria-invalid={errorFields.has(spec.name) || undefined}
            className={secret ? "pr-10" : undefined}
          />
          {secret && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full w-10 text-muted-foreground"
              onClick={() =>
                setRevealed((current) => ({
                  ...current,
                  [spec.name]: !current[spec.name],
                }))
              }
              aria-label={t(
                visible
                  ? "tools.integrations.configurator.hide"
                  : "tools.integrations.configurator.show",
              )}
            >
              {visible ? (
                <EyeSlashIcon className="h-4 w-4" />
              ) : (
                <EyeIcon className="h-4 w-4" />
              )}
            </Button>
          )}
        </div>
      </Field>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <IntegrationLogo id={integration} size="lg" />
            <div className="min-w-0 text-left">
              <DialogTitle>
                {t(
                  accountId
                    ? "tools.integrations.configurator.editTitle"
                    : "tools.integrations.configurator.title",
                  { name },
                )}
              </DialogTitle>
              <DialogDescription>
                {t(
                  `tools.integrations.configurator.${integration}.description`,
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            // The form is inside the tool's page form: never let it submit that one.
            e.stopPropagation();
            if (savedId) finish(savedId);
            else if (!missingRequired && !save.isPending) void runSave();
          }}
        >
          {fields.filter((spec) => !spec.deduced).map(renderField)}

          {hasDeduced && !showDeduced && (
            <p className="text-sm text-muted-foreground">
              {t(`tools.integrations.configurator.${integration}.deduced`)}{" "}
              <Button
                type="button"
                variant="link"
                size="inline"
                className="h-auto p-0 text-sm"
                onClick={() => setManual(true)}
              >
                {t("tools.integrations.configurator.manual")}
              </Button>
            </p>
          )}

          {hasDeduced && showDeduced && (
            <div className="space-y-4 rounded-md border border-dashed p-4">
              <p className="text-sm text-muted-foreground">
                {result?.recognized === false
                  ? t(
                      `tools.integrations.configurator.${integration}.unrecognized`,
                    )
                  : t(`tools.integrations.configurator.${integration}.manual`)}
              </p>
              {fields.filter((spec) => spec.deduced).map(renderField)}
            </div>
          )}

          <CheckOutcome
            integration={integration}
            result={result}
            failed={save.isError}
          />

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t("tools.integrations.configurator.cancel")}
            </Button>
            {result?.error?.code === "unreachable" && !savedId && (
              <Button
                type="button"
                variant="outline"
                disabled={save.isPending}
                onClick={() => void runSave(true)}
              >
                {t("tools.integrations.configurator.applyUnchecked")}
              </Button>
            )}
            {savedId ? (
              <Button type="submit">
                {t("tools.integrations.configurator.done")}
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={missingRequired || save.isPending}
              >
                {save.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                {t(
                  accountId
                    ? "tools.integrations.configurator.checkAndUpdate"
                    : "tools.integrations.configurator.checkAndConnect",
                )}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** What the check found: the proof that the account works, or why it does not. */
function CheckOutcome({
  integration,
  result,
  failed,
}: {
  integration: CheckedIntegration;
  result: IntegrationCheck | null;
  failed: boolean;
}) {
  const { t } = useTranslation("tools");
  const name = t(`tools.integrations.${integration}.name`);

  if (failed) {
    return (
      <Outcome tone="error">
        {t("tools.integrations.check.requestFailed")}
      </Outcome>
    );
  }
  if (!result) return null;

  if (!result.ok) {
    const code = result.error?.code ?? "unreachable";
    return (
      <Outcome tone="error">
        {code === "incomplete"
          ? t("tools.integrations.check.incomplete")
          : t(`tools.integrations.check.errors.${code}`, { name })}
      </Outcome>
    );
  }

  const details = result.details ?? {};
  const lines: string[] = [];
  if (integration === "provalliance_backoffice") {
    lines.push(
      t("tools.integrations.check.ok.provalliance_backoffice", {
        brand: details.brand || name,
        salon: details.salon_id,
      }),
    );
    if (details.hairdressers?.length) {
      lines.push(
        t("tools.integrations.check.ok.hairdressers", {
          count: details.hairdressers.length,
          names: details.hairdressers.join(", "),
        }),
      );
    }
  } else if (integration === "calendly") {
    lines.push(
      t("tools.integrations.check.ok.calendly", {
        account: details.account,
        email: details.email,
        count: details.event_types ?? 0,
      }),
    );
  } else {
    lines.push(
      t("tools.integrations.check.ok.virtual_browser", { host: details.host }),
    );
    if (details.native) {
      lines.push(
        t("tools.integrations.check.ok.native", {
          name: t(`tools.integrations.${details.native}.name`, details.native),
        }),
      );
    }
  }

  return (
    <Outcome tone="success">
      {lines.map((line) => (
        <p key={line}>{line}</p>
      ))}
    </Outcome>
  );
}

function Outcome({
  tone,
  children,
}: {
  tone: "success" | "error";
  children: React.ReactNode;
}) {
  const Icon = tone === "success" ? CheckCircleIcon : WarningCircleIcon;
  return (
    <div
      role="status"
      className={
        tone === "success"
          ? "flex gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-100"
          : "flex gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
      }
    >
      <Icon weight="fill" className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="space-y-1">{children}</div>
    </div>
  );
}
